import { datadogRum } from "@datadog/browser-rum";
import { wobaLogger } from "@/config/woba-logger";

/**
 * Known async operations in the Vision AI wizard + proposal flow that we
 * instrument for observability. Add a new member here when instrumenting a new
 * failure point (keep it in sync with the actual call sites — no placeholders).
 */
export type ObservabilityOperation =
  | "createProposal"
  | "updateProposal"
  | "importDeal"
  | "getImportDealBrief"
  | "searchAndRank"
  | "searchMode"
  | "getProposal"
  | "getPois"
  | "notifySlack"
  | "createTicketRequest"
  | "completeTtoMetric"
  | "rateSpace"
  | "logQuickSearch";

/**
 * Non-sensitive context allowed to accompany an observability signal.
 *
 * IMPORTANT: this is the single choke point for what reaches Datadog. Only
 * non-identifying identifiers are permitted — NEVER add contact details,
 * budget/pricing, seller notes, or raw request/response payloads. Both the
 * closed type and the runtime allowlist below enforce this.
 */
export interface ObservabilityContext {
  operation: ObservabilityOperation;
  step?: string;
  dealId?: string | number;
  proposalId?: string;
  spaceId?: string | number;
  httpStatus?: number;
  durationMs?: number;
}

const ALLOWED_KEYS: (keyof ObservabilityContext)[] = [
  "operation",
  "step",
  "dealId",
  "proposalId",
  "spaceId",
  "httpStatus",
  "durationMs",
];

/**
 * Picks only the allowlisted keys, dropping anything else (and undefined
 * values). Runtime guarantee that no unexpected field — e.g. PII smuggled in
 * via a widened type — is ever forwarded to Datadog.
 */
function sanitizeContext(
  context: ObservabilityContext,
): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const key of ALLOWED_KEYS) {
    const value = context[key];
    if (value !== undefined) {
      safe[key] = value;
    }
  }
  return safe;
}

/**
 * Report a caught error to Datadog with sanitized context. Use inside catch
 * blocks; existing user-facing handling (toasts) stays as-is.
 */
export function reportError(
  error: unknown,
  context: ObservabilityContext,
): void {
  const safeContext = sanitizeContext(context);
  // Structured log — Datadog Logs in prod (with redaction), console otherwise.
  wobaLogger.error(
    `Vision operation failed: ${context.operation}`,
    error,
    safeContext,
  );
  // RUM Error Tracking — grouped and alertable, with business context.
  datadogRum.addError(error, safeContext);
}

/**
 * Report a non-exception failure signal (e.g. a broken image URL) as a Datadog
 * action. Use when there is no Error object to attach.
 */
export function reportEvent(name: string, context: ObservabilityContext): void {
  datadogRum.addAction(name, sanitizeContext(context));
}
